import './index.css';
import './shared/styles/theme.css';
import { setupProductionConsole, devError, devLog, devWarn } from './shared/lib/devLog';
import { applyDesignConfig } from './shared/theme/applyDesignConfig';
import { initializeTheme } from './shared/design/colors/applyTheme';
import { detectPlatform } from './platform/detectPlatform';
import { WebAdapter } from './platform/adapters/web.adapter';
import { VKAdapter } from './platform/adapters/vk.adapter';
import { TelegramAdapter } from './platform/adapters/telegram.adapter';
import { setAppHeight } from './shared/utils/viewport';
import { markAssetRecoverySuccess, tryRecoverFromAssetMismatch } from './shared/lib/assetRecovery';
import { resolveApiBaseUrl } from '@minibook/shared';
import { bootstrapOnboardingTrace } from './shared/analytics/onboardingTraceBootstrap';
import { ensurePwaInstallEventListeners } from './shared/pwa/pwaInstallSingleton';
import { initSentryBrowser } from '@minibook/shared/client/sentryBrowser';

setupProductionConsole();
initSentryBrowser();

// Monkey patch addEventListener to add passive: true for wheel events
const originalAddEventListener = EventTarget.prototype.addEventListener;
EventTarget.prototype.addEventListener = function(type: string, listener: any, options?: any) {
  if (type === 'wheel' && (!options || (typeof options === 'object' && options.passive !== false))) {
    // Add passive: true for wheel events to prevent violation
    const passiveOptions = typeof options === 'object' ? { ...options, passive: true } : { passive: true };
    return originalAddEventListener.call(this, type, listener, passiveOptions);
  }
  return originalAddEventListener.call(this, type, listener, options);
};

// Suppress device-orientation warnings from Telegram WebApp
if (navigator.permissions && navigator.permissions.query) {
  const originalQuery = navigator.permissions.query;
  navigator.permissions.query = function(descriptor: any) {
    if (descriptor.name === 'accelerometer' || descriptor.name === 'gyroscope') {
      // Return denied permission to suppress Telegram WebApp warnings
      return Promise.resolve({ state: 'denied' } as any);
    }
    return originalQuery.call(this, descriptor);
  };
}

// Блокировка ориентации в портрет.
// На части устройств display-mode определяется нестабильно, поэтому пробуем повторно
// при фокусе/возврате в приложение.
function tryLockPortrait() {
  const o = (screen as { orientation?: { lock?: (mode: string) => Promise<void> } }).orientation;
  if (!o?.lock) return;
  // portrait-primary сначала, затем общий portrait как fallback.
  o.lock('portrait-primary').catch(() => {
    o.lock?.('portrait').catch(() => {});
  });
}

if (typeof window !== 'undefined') {
  bootstrapOnboardingTrace();
  tryLockPortrait();
  window.addEventListener('focus', tryLockPortrait);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      tryLockPortrait();
    }
  });

  setAppHeight();
  window.addEventListener('resize', () => setAppHeight());
  window.visualViewport?.addEventListener?.('resize', () => setAppHeight());
  window.visualViewport?.addEventListener?.('scroll', () => setAppHeight());
}

async function cleanupTelegramServiceWorkers(): Promise<void> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch (error) {
      devWarn('[main] Failed to unregister service workers for Telegram', error);
    }
  }

  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    } catch (error) {
      devWarn('[main] Failed to clear Cache API for Telegram', error);
    }
  }
}

// Выводим информацию о сборке (VITE_APP_VERSION передаётся при деплое; VITE_BUILD_VERSION — опционально из package.json)
const buildVersion = import.meta.env.VITE_BUILD_VERSION || import.meta.env.VITE_APP_VERSION || 'dev';
const gitCommit = import.meta.env.VITE_GIT_COMMIT || 'unknown';
const gitBranch = import.meta.env.VITE_GIT_BRANCH || 'unknown';
const buildDate = import.meta.env.VITE_BUILD_DATE || 'unknown';
const apiUrl = resolveApiBaseUrl({
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
  apiUrl: import.meta.env.VITE_API_URL,
}) || 'unknown';

// Определяем окружение фронтенда (используем VITE_STAGE для консистентности с UI)
const STAGE_RAW = (import.meta.env.VITE_STAGE || (import.meta.env.PROD ? 'prod' : 'dev')).toLowerCase();
const frontendEnv = STAGE_RAW.includes('dev') ? 'DEV' : 'PROD';

// Определяем окружение бэкенда по URL
let backendEnv = 'unknown';
if (apiUrl.includes('api-dev.beclients.')) {
  backendEnv = 'DEV';
} else if (apiUrl.includes('api.beclients.')) {
  backendEnv = 'PROD';
} else if (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')) {
  backendEnv = 'LOCAL';
} else {
  backendEnv = 'CUSTOM';
}

devLog('%c📦 Build Info', 'color: #4CAF50; font-weight: bold; font-size: 14px;');
devLog('  Version:', buildVersion);
devLog('  Git Commit:', gitCommit);
devLog('  Git Branch:', gitBranch);
devLog('  Build Date:', buildDate);
devLog('  Frontend:', frontendEnv);
devLog('  Backend:', backendEnv, `(${apiUrl})`);
if (import.meta.env.DEV) {
  devLog('  → Запросы уходят на этот URL. Чтобы бить в dev API, в .env.local задай VITE_API_URL=https://api-dev.beclients.ru или https://api-dev.beclients.pro и перезапусти dev-сервер (без VITE_API_* в process.env).');
}

// Определяем платформу (единый путь: platform/*)
const platformDetect = detectPlatform();
const platform = platformDetect.platform;
const buildTarget = String(import.meta.env.VITE_BUILD_TARGET || 'web').toLowerCase();

// PWA: перехват beforeinstallprompt до React — иначе событие часто уже ушло и prompt() недоступен.
if (typeof window !== 'undefined' && platform === 'web' && buildTarget === 'web') {
  ensurePwaInstallEventListeners();
}

// SW только для кэша; обновится при следующем запуске (registerType: 'autoUpdate').
if (platform === 'web' && buildTarget === 'web' && 'serviceWorker' in navigator) {
  import('virtual:pwa-register')
    .then(({ registerSW }) => {
      registerSW({ immediate: true });
    })
    .catch((error) => {
      devWarn('[main] Failed to load virtual:pwa-register', error);
      void tryRecoverFromAssetMismatch(error);
    });
}

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    void tryRecoverFromAssetMismatch(event.reason);
  });
}

// Platform-specific инициализация и dynamic import
(async () => {
  try {
    devLog('[main] Platform detected:', platform);
    
    if (platform === 'web') {
      devLog('[main] Rendering web app (email/password auth)...');
      initializeTheme();
      applyDesignConfig();
      const { renderWebApp } = await import('./entry/webApp');
      const adapterOverride = new WebAdapter();
      renderWebApp({ platformOverride: platform, adapterOverride });
      markAssetRecoverySuccess();
      return;
    }
    if (platform === 'vk') {
      devLog('[main] Initializing VK platform...');
      initializeTheme();
      applyDesignConfig();
      const { renderVKApp } = await import('./entry/vkApp');
      const adapterOverride = new VKAdapter();
      await renderVKApp({ platformOverride: platform as any, adapterOverride });
      markAssetRecoverySuccess();
      return;
    }

    if (platform === 'telegram') {
      devLog('[main] Initializing Telegram platform...');
      await cleanupTelegramServiceWorkers();
      const script = document.createElement('script');
      script.src = 'https://telegram.org/js/telegram-web-app.js';
      document.head.appendChild(script);
      await new Promise<void>((resolve) => {
        script.onload = () => resolve();
        script.onerror = () => resolve();
      });
      initializeTheme();
      applyDesignConfig();
      const { renderTelegramApp } = await import('./entry/telegramApp');
      const adapterOverride = new TelegramAdapter();
      renderTelegramApp({ platformOverride: platform as any, adapterOverride });
      markAssetRecoverySuccess();
      return;
    }

    // ok, max (dev override) или неизвестная платформа → web
    devLog('[main] Fallback to web app (platform:', platform, ')');
    initializeTheme();
    applyDesignConfig();
    const { renderWebApp } = await import('./entry/webApp');
    const adapterOverride = new WebAdapter();
    renderWebApp({ platformOverride: platform, adapterOverride });
    markAssetRecoverySuccess();
  } catch (error) {
    if (await tryRecoverFromAssetMismatch(error)) {
      return;
    }
    devError('[main] Fatal error during initialization:', error);
    // Показываем ошибку пользователю
    document.body.innerHTML = `
      <div style="padding: 20px; font-family: sans-serif;">
        <h1>Ошибка загрузки приложения</h1>
        <p>${error instanceof Error ? error.message : String(error)}</p>
        <pre style="background: #f5f5f5; padding: 10px; overflow: auto;">${error instanceof Error ? error.stack : String(error)}</pre>
      </div>
    `;
  }
})();
